import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Sparkles, Target, ShieldAlert, TrendingUp, TrendingDown, Share2, Bell,
  Loader2, GraduationCap, Zap, ChevronRight, Info, ChevronDown, Activity,
  BarChart3, Clock, DollarSign, CheckCircle2, Link2,
} from "lucide-react";
import { toast } from "sonner";
import CandlestickChart from "./CandlestickChart";

const F = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));
const money = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Translate pro-level chart signals (trend, volume, timing, position sizing)
 * into plain-English insight rows for beginner mode.  Every card the pro sees
 * we ALSO show to the beginner - just phrased like a coach would.
 */
function TranslatedInsights({ candles, plan, kind, currentPrice, cashBalance, patternName, onSetAlert }) {
  const insights = useMemo(() => {
    if (!candles || candles.length < 20 || !plan) return [];
    const out = [];
    // -- Trend (SMA20 vs SMA50) ----------------------------------------------
    const closes = candles.map((c) => c.c);
    const sma = (n) => {
      const s = closes.slice(-n);
      return s.reduce((a, b) => a + b, 0) / s.length;
    };
    if (closes.length >= 50) {
      const s20 = sma(20), s50 = sma(50);
      const diff = ((s20 - s50) / s50) * 100;
      const strong = Math.abs(diff) > 1.5;
      const up = s20 > s50;
      out.push({
        icon: up ? TrendingUp : TrendingDown,
        color: up ? "hsl(var(--gain))" : "hsl(var(--loss))",
        label: "Overall trend",
        value: up ? (strong ? "Strong uptrend" : "Mild uptrend") : (strong ? "Strong downtrend" : "Mild downtrend"),
        detail: up
          ? "Prices have been rising over the past month. Buying with the trend usually works better than fighting it."
          : "Prices have been falling over the past month. Be extra careful — buying into a downtrend is risky.",
        aligned: (up && kind === "bull") || (!up && kind === "bear"),
      });
    }
    // -- Volume health -------------------------------------------------------
    const vols = candles.map((c) => c.v || 0).filter((v) => v > 0);
    if (vols.length >= 10) {
      const recent = vols.slice(-3).reduce((a, b) => a + b, 0) / 3;
      const avg = vols.slice(-30, -3).reduce((a, b) => a + b, 0) / Math.max(1, vols.slice(-30, -3).length);
      const ratio = recent / (avg || 1);
      const healthy = ratio >= 0.9;
      const strong = ratio >= 1.3;
      out.push({
        icon: BarChart3,
        color: strong ? "hsl(var(--gain))" : healthy ? "hsl(var(--ai))" : "hsl(var(--loss))",
        label: "Trader interest",
        value: strong ? "Very high — traders engaged" : healthy ? "Normal — plenty of activity" : "Low — thin trading",
        detail: strong
          ? "Volume is well above average. Lots of buyers and sellers = your order will fill smoothly."
          : healthy
            ? "Trading volume looks healthy. You should be able to buy or sell without moving the price much."
            : "Volume is thin. Prices can jump around unpredictably — consider waiting for more activity.",
      });
    }
    // -- Timing (how far from entry) ----------------------------------------
    if (currentPrice && plan.entry) {
      const dist = ((currentPrice - plan.entry) / plan.entry) * 100;
      const abs = Math.abs(dist);
      const isBull = kind === "bull";
      // For bullish: waiting to buy when price drops TO entry; for bearish, waiting to short when price rises TO entry
      const readyToEnter = isBull ? currentPrice <= plan.entry * 1.005 : currentPrice >= plan.entry * 0.995;
      out.push({
        icon: readyToEnter ? CheckCircle2 : Clock,
        color: readyToEnter ? "hsl(var(--gain))" : "hsl(var(--ai))",
        label: "Timing",
        value: readyToEnter ? "Ready — price is at your entry" : abs < 2 ? `Almost there — ${abs.toFixed(1)}% away` : `Wait — ${abs.toFixed(1)}% away from entry`,
        detail: readyToEnter
          ? "Price has reached the buy zone. If you want to trade this setup, now is the moment."
          : "Set a price alert. Don't chase — let the price come to you.",
        cta: !readyToEnter ? { label: "Set alert at $" + plan.entry.toFixed(2), onClick: onSetAlert } : null,
      });
    }
    // -- Position sizing -----------------------------------------------------
    if (cashBalance && plan.entry && plan.stop) {
      const riskPerShare = Math.abs(plan.entry - plan.stop);
      // Beginner-safe: 1% of account at risk per trade
      const maxRiskUsd = cashBalance * 0.01;
      const suggestedShares = Math.max(0.01, Math.floor((maxRiskUsd / riskPerShare) * 100) / 100);
      const positionValue = suggestedShares * plan.entry;
      const potentialGain = suggestedShares * Math.abs(plan.target - plan.entry);
      out.push({
        icon: DollarSign,
        color: "hsl(var(--foreground))",
        label: "How much to buy",
        value: `${suggestedShares.toFixed(suggestedShares >= 10 ? 0 : 2)} shares ≈ $${positionValue.toFixed(0)}`,
        detail: `That risks $${maxRiskUsd.toFixed(0)} (1% of your account). If the trade hits target, you'd earn about $${potentialGain.toFixed(0)}.`,
        emphasis: true,
      });
    }
    // -- Pattern reliability -------------------------------------------------
    const patternReliability = {
      "Bull Flag": { rate: 65, note: "Bull flags in uptrends continue about 65% of the time (Bulkowski 2005)." },
      "Bear Flag": { rate: 60, note: "Bear flags continue in the downtrend about 60% of the time historically." },
      "Double Bottom": { rate: 63, note: "Double bottoms reverse to the upside about 63% of the time." },
      "Double Top":    { rate: 61, note: "Double tops reverse to the downside about 61% of the time." },
      "Breakout (bull)": { rate: 58, note: "Breakouts with above-average volume follow through ~58% of the time." },
      "Breakdown (bear)": { rate: 58, note: "Breakdowns with above-average volume follow through ~58% of the time." },
    };
    const rel = patternReliability[patternName];
    if (rel) {
      out.push({
        icon: Activity,
        color: "hsl(var(--ai))",
        label: "Pattern reliability",
        value: `${rel.rate}% historical win rate`,
        detail: rel.note + " Not a guarantee — always use the stop-loss.",
      });
    }
    return out;
  }, [candles, plan, kind, currentPrice, cashBalance, patternName, onSetAlert]);

  if (!insights.length) return null;
  return (
    <Card className="p-5 rounded-2xl" data-testid="copilot-insights">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Reading the chart · in plain English</div>
      <div className="space-y-2.5">
        {insights.map((ins, i) => {
          const Icon = ins.icon;
          return (
            <div key={i} data-testid={`insight-${i}`}
                 className={`flex items-start gap-3 p-3 rounded-xl ${ins.emphasis ? "bg-secondary/60 border border-border" : "bg-secondary/30"}`}>
              <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: ins.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{ins.label}</div>
                  {ins.aligned != null && (
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                      ins.aligned ? "bg-[hsl(var(--gain))]/20 text-[hsl(var(--gain))]" : "bg-[hsl(var(--loss))]/20 text-[hsl(var(--loss))]"
                    }`}>{ins.aligned ? "Trade with trend" : "Against trend"}</span>
                  )}
                </div>
                <div className="text-sm font-semibold mt-0.5" style={{ color: ins.color }}>{ins.value}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{ins.detail}</div>
                {ins.cta && (
                  <button onClick={ins.cta.onClick}
                          className="mt-2 text-xs font-medium text-[hsl(var(--ai))] hover:underline flex items-center gap-1"
                          data-testid={`insight-cta-${i}`}>
                    <Bell className="w-3 h-3" /> {ins.cta.label}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* One-word verdict (STRONG / DECENT / SKIP) — the entire beginner headline */
function Verdict({ score, kind }) {
  const isBull = kind === "bull";
  const verb = isBull ? "BUY" : "SHORT";
  const strong = score >= 70;
  const decent = score >= 50 && !strong;
  const color = strong ? "hsl(var(--gain))" : decent ? "hsl(var(--ai))" : "hsl(var(--loss))";
  const label = strong ? `STRONG ${verb}` : decent ? `DECENT ${verb}` : "SKIP THIS";
  return (
    <div className="flex items-center gap-3" data-testid="copilot-verdict">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-bold"
           style={{ backgroundColor: `${color}20`, color, border: `2px solid ${color}` }}>
        {score}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Verdict</div>
        <div className="font-display text-2xl md:text-3xl font-black" style={{ color }}>{label}</div>
      </div>
    </div>
  );
}

/**
 * Chart Copilot — beginner-first setup card.
 *  - Beginner mode: verdict + one-sentence plan + one big button + collapsible "Why?"
 *  - Pro mode: full narration + alternates + swing markers
 */
export default function ChartCopilot({ symbol, assetType, currentPrice, onPrefillOrder }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [beginner, setBeginner] = useState(() => localStorage.getItem("aanar_copilot_beginner") !== "false");
  const [whyOpen, setWhyOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [sending, setSending] = useState(false);
  const chartRef = useRef(null);

  const setPriceAlert = async () => {
    if (!data?.best?.plan?.entry) return;
    const isBull = data.best.kind === "bull";
    try {
      await api.post("/alerts/rules", {
        asset_type: assetType,
        symbol,
        indicator: "price",
        operator: isBull ? "<=" : ">=",
        threshold: data.best.plan.entry,
        note: `Copilot entry: ${data.best.name}`,
        channels: ["web"],
      });
      toast.success(`Alert set — we'll ping you when ${symbol.toUpperCase()} reaches $${data.best.plan.entry.toFixed(2)}`);
    } catch (e) {
      toast.error("Couldn't set alert");
    }
  };

  const sendToIbkr = async () => {
    if (!best || !plan) return;
    setSending(true);
    try {
      // Beginner-safe: 1% of account per trade, sized off the stop-loss
      const cash = user?.cash_balance || 0;
      const riskPerShare = Math.abs(plan.entry - plan.stop);
      const qty = cash > 0 && riskPerShare > 0
        ? Math.max(1, Math.floor((cash * 0.01) / riskPerShare))
        : 1;
      await api.post("/ibkr/mcp/draft", {
        symbol,
        side: isBull ? "BUY" : "SELL",
        quantity: qty,
        order_type: "LMT",
        limit_price: plan.entry,
        tif: "DAY",
      });
      toast.success(
        `Draft sent to IBKR · ${qty} sh ${symbol.toUpperCase()} @ ${money(plan.entry)}. Open IBKR → AI Instructions to confirm.`,
        { duration: 8000 },
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || "";
      if (msg.includes("not connected")) {
        toast.error("Connect IBKR in Settings first", {
          action: { label: "Go to Settings", onClick: () => window.location.assign("/settings") },
        });
      } else {
        toast.error("Couldn't send draft: " + msg);
      }
    } finally { setSending(false); }
  };

  useEffect(() => {
    localStorage.setItem("aanar_copilot_beginner", beginner ? "true" : "false");
  }, [beginner]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/copilot/${assetType}/${symbol}?range=3M`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [symbol, assetType]);

  const best = data?.best;
  const plan = best?.plan;
  const candles = data?.candles || [];
  const isBull = best?.kind === "bull";

  /* -------- Share card ---------------------------------------------------- */
  const shareSetup = async () => {
    if (!best || !plan) return;
    setSharing(true);
    try {
      // Grab the chart SVG that's already on the page
      const chartSvg = chartRef.current?.querySelector("svg");
      if (!chartSvg) throw new Error("No chart to share");

      const cardW = 1200, cardH = 800;
      const isBullK = best.kind === "bull";
      const verb = isBullK ? "BUY" : "SHORT";
      const verdictColor = best.confidence >= 70 ? "#22c55e" : best.confidence >= 50 ? "#38bdf8" : "#ef4444";
      const verdictLabel = best.confidence >= 70 ? `STRONG ${verb}` : best.confidence >= 50 ? `DECENT ${verb}` : "SKIP";

      // Build a self-contained shareable SVG - Aanar branding + chart + trade plan
      const cleanSym = assetType === "crypto"
        ? symbol.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
        : symbol.toUpperCase();

      const chartXml = new XMLSerializer().serializeToString(chartSvg.cloneNode(true));
      // CSS custom properties (hsl(var(--*))) don't resolve when the SVG is
      // rendered offscreen via drawImage. Substitute them with concrete values
      // so the exported card is fully self-contained and legible on any device.
      const cssColors = {
        "hsl(var(--gain))": "#22c55e",
        "hsl(var(--loss))": "#ef4444",
        "hsl(var(--ai))":   "#38bdf8",
        "hsl(var(--card))": "#0b0b12",
        "hsl(var(--border))": "#27272a",
        "hsl(var(--muted-foreground))": "#a1a1aa",
        "hsl(var(--foreground))": "#fafafa",
      };
      let inlinedXml = chartXml;
      for (const [k, v] of Object.entries(cssColors)) {
        inlinedXml = inlinedXml.split(k).join(v);
      }
      const chartDataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(inlinedXml);

      // Load chart as image so we can draw it into canvas
      const chartImg = new Image();
      await new Promise((res, rej) => { chartImg.onload = res; chartImg.onerror = rej; chartImg.src = chartDataUrl; });

      const canvas = document.createElement("canvas");
      canvas.width = cardW * 2; canvas.height = cardH * 2; // 2x for retina
      const ctx = canvas.getContext("2d");
      ctx.scale(2, 2);

      // Background
      const grad = ctx.createLinearGradient(0, 0, cardW, cardH);
      grad.addColorStop(0, "#0a0a0f");
      grad.addColorStop(1, "#111119");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, cardW, cardH);

      // Load the Aanar app icon (light version = white background) so the
      // mountain-peak mark pops against the dark share card.
      const markImg = new Image();
      await new Promise((res) => {
        markImg.onload = res; markImg.onerror = res;
        markImg.src = "/brand/aanar-icon-180.png";
      });

      // Subtle accent stripe
      ctx.fillStyle = verdictColor + "22";
      ctx.fillRect(0, 0, 8, cardH);

      // Aanar logo mark (white bg app icon, so it stands out on black)
      if (markImg.complete && markImg.naturalWidth > 0) {
        ctx.save();
        // Draw with rounded corners for that app-icon look
        const iconX = 36, iconY = 28, iconSize = 56, iconR = 12;
        ctx.beginPath();
        ctx.moveTo(iconX + iconR, iconY);
        ctx.arcTo(iconX + iconSize, iconY, iconX + iconSize, iconY + iconSize, iconR);
        ctx.arcTo(iconX + iconSize, iconY + iconSize, iconX, iconY + iconSize, iconR);
        ctx.arcTo(iconX, iconY + iconSize, iconX, iconY, iconR);
        ctx.arcTo(iconX, iconY, iconX + iconSize, iconY, iconR);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(markImg, iconX, iconY, iconSize, iconSize);
        ctx.restore();
      }
      // Wordmark
      ctx.fillStyle = "#ffffff";
      ctx.font = "900 34px Outfit, system-ui, sans-serif";
      ctx.fillText("Aanar", 108, 62);
      ctx.fillStyle = "#71717a";
      ctx.font = "700 11px Outfit, system-ui, sans-serif";
      ctx.fillText("OWN THE MARKET", 108, 80);

      // Ticker + pattern
      ctx.fillStyle = "#ffffff";
      ctx.font = "900 72px Outfit, system-ui, sans-serif";
      ctx.fillText(cleanSym, 40, 170);
      ctx.fillStyle = verdictColor;
      ctx.font = "700 24px Outfit, system-ui, sans-serif";
      ctx.fillText(best.name.toUpperCase(), 40, 210);

      // Verdict pill (top-right)
      const pillX = cardW - 260, pillY = 100, pillW = 220, pillH = 100;
      ctx.fillStyle = verdictColor + "22";
      ctx.strokeStyle = verdictColor;
      ctx.lineWidth = 3;
      // rounded rect polyfill
      const rr = (x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
      };
      rr(pillX, pillY, pillW, pillH, 20); ctx.fill(); ctx.stroke();
      ctx.fillStyle = verdictColor;
      ctx.font = "900 48px Outfit, system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`${best.confidence}%`, pillX + pillW / 2, pillY + 55);
      ctx.font = "700 14px Outfit, system-ui, sans-serif";
      ctx.fillText(verdictLabel, pillX + pillW / 2, pillY + 80);
      ctx.textAlign = "left";

      // Chart image (middle band)
      const chartY = 220, chartH = 320;
      ctx.drawImage(chartImg, 40, chartY, cardW - 80, chartH);

      // Trade plan strip
      const stripY = 580;
      const cellW = (cardW - 80) / 4;
      const cells = [
        { label: isBullK ? "BUY AT" : "SHORT AT", value: money(plan.entry), color: "#22c55e" },
        { label: "STOP LOSS", value: money(plan.stop), color: "#ef4444" },
        { label: "TARGET", value: money(plan.target), color: "#38bdf8" },
        { label: "RISK / REWARD", value: `1 : ${F(plan.rr, 2)}`, color: "#ffffff" },
      ];
      cells.forEach((c, i) => {
        const x = 40 + i * cellW;
        ctx.fillStyle = c.color + "18";
        ctx.strokeStyle = c.color + "77";
        ctx.lineWidth = 2;
        rr(x, stripY, cellW - 12, 110, 16); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "#a1a1aa";
        ctx.font = "700 12px Outfit, system-ui, sans-serif";
        ctx.fillText(c.label, x + 20, stripY + 32);
        ctx.fillStyle = c.color;
        ctx.font = "800 32px 'JetBrains Mono', monospace";
        ctx.fillText(c.value, x + 20, stripY + 78);
      });

      // Footer
      ctx.fillStyle = "#52525b";
      ctx.font = "500 14px Outfit, system-ui, sans-serif";
      ctx.fillText("Not financial advice · Read the price line · aanar.app", 40, cardH - 30);
      ctx.textAlign = "right";
      const dateStr = new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      ctx.fillText(dateStr, cardW - 40, cardH - 30);
      ctx.textAlign = "left";

      // Export
      canvas.toBlob(async (blob) => {
        if (!blob) throw new Error("Blob failed");
        const filename = `aanar-${cleanSym}-${best.kind}-${Date.now()}.png`;
        const file = new File([blob], filename, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: `${cleanSym} · ${best.name}`,
              text: `${verdictLabel} setup on ${cleanSym} — spotted by Aanar`,
            });
            toast.success("Setup shared");
            return;
          } catch { /* fall through to download */ }
        }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 500);
        toast.success("Setup card saved to Downloads");
      }, "image/png");
    } catch (e) {
      toast.error("Couldn't build the share card");
    } finally {
      setSharing(false);
    }
  };
  /* ----------------------------------------------------------------------- */

  if (loading) {
    return (
      <Card className="p-5 rounded-2xl flex items-center justify-center min-h-[240px]" data-testid="copilot-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        <span className="text-sm text-muted-foreground">Scanning chart for setups…</span>
      </Card>
    );
  }

  /* One-sentence plan the beginner reads and acts on */
  const planSentence = plan && best ? (
    isBull
      ? `Buy ${symbol.toUpperCase()} at ${money(plan.entry)}. If it drops to ${money(plan.stop)}, sell to limit your loss. Your target: ${money(plan.target)} (1:${F(plan.rr, 2)} risk-to-reward).`
      : `Short ${symbol.toUpperCase()} at ${money(plan.entry)}. If it rises to ${money(plan.stop)}, exit to limit your loss. Your target: ${money(plan.target)} (1:${F(plan.rr, 2)} risk-to-reward).`
  ) : "";

  return (
    <div className="space-y-4" data-testid="copilot-panel">
      {/* Header — beginner/pro toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Chart Copilot</div>
          <div className="flex items-center gap-2 mt-0.5">
            <Sparkles className="w-5 h-5 text-[hsl(var(--ai))]" />
            <h2 className="font-display text-xl md:text-2xl font-bold">What I see on this chart</h2>
          </div>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-secondary text-xs" data-testid="copilot-mode-toggle">
          <button
            onClick={() => setBeginner(true)}
            data-testid="copilot-mode-beginner"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
              beginner ? "bg-[hsl(var(--ai))] text-background font-semibold" : "text-muted-foreground hover:text-foreground"
            }`}>
            <GraduationCap className="w-3.5 h-3.5" /> Beginner
          </button>
          <button
            onClick={() => setBeginner(false)}
            data-testid="copilot-mode-pro"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
              !beginner ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:text-foreground"
            }`}>
            <Zap className="w-3.5 h-3.5" /> Pro
          </button>
        </div>
      </div>

      {/* No setup */}
      {!best && (
        <Card className="p-6 rounded-2xl text-center" data-testid="copilot-no-setup">
          <Info className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <div className="font-display text-lg">No clear setup right now</div>
          <div className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            The chart is choppy. Best move: <span className="text-foreground font-medium">wait</span>.
            Great traders skip more trades than they take.
          </div>
        </Card>
      )}

      {best && (
        <>
          {/* Chart with trade plan overlay */}
          <Card className="p-3 rounded-2xl" data-testid="copilot-chart-card" ref={chartRef}>
            <CandlestickChart
              candles={candles}
              patterns={beginner ? [] : [best]}
              activeIndicators={beginner ? [] : ["sma20", "sma50"]}
              tradePlan={plan ? { ...plan, points: plan.points || [], zones: data.zones, kind: best.kind } : null}
              height={340}
              showVolume={!beginner}
              symbol={`${assetType}:${symbol}`}
            />
          </Card>

          {/* BEGINNER MODE — 3 blocks only: verdict, plan, actions */}
          {beginner ? (
            <>
              {/* Verdict + pattern name */}
              <Card className="p-5 rounded-2xl" data-testid="copilot-headline">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <Verdict score={best.confidence} kind={best.kind} />
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pattern</div>
                    <div className="font-display text-lg font-semibold">{best.name}</div>
                  </div>
                </div>
                {/* ONE sentence plan */}
                <p className="mt-4 text-base md:text-lg leading-relaxed" data-testid="copilot-plan-sentence">
                  {planSentence}
                </p>
              </Card>

              {/* Actions row */}
              <div className="flex flex-col sm:flex-row gap-2">
                {onPrefillOrder && plan && (
                  <Button
                    data-testid="copilot-prefill-btn"
                    onClick={() => onPrefillOrder({ side: isBull ? "buy" : "sell", price: plan.entry, stop: plan.stop })}
                    size="lg"
                    className={`rounded-full flex-1 h-14 text-base font-bold ${isBull ? "bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90" : "bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90"} text-background`}>
                    <Target className="w-5 h-5 mr-2" />
                    {isBull ? `Paper buy ${symbol.toUpperCase()} at ${money(plan.entry)}` : `Paper short ${symbol.toUpperCase()} at ${money(plan.entry)}`}
                  </Button>
                )}
                <Button
                  onClick={sendToIbkr} disabled={sending}
                  size="lg" variant="outline"
                  data-testid="copilot-ibkr-btn"
                  className="rounded-full h-14 border-2 border-[hsl(var(--ai))] text-[hsl(var(--ai))] hover:bg-[hsl(var(--ai))]/10 font-semibold">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                  <span className="hidden sm:inline">Send to IBKR</span>
                  <span className="sm:hidden">IBKR</span>
                </Button>
                <Button
                  onClick={shareSetup} disabled={sharing}
                  variant="outline" size="lg"
                  data-testid="copilot-share-btn"
                  className="rounded-full h-14">
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4 sm:mr-2" />}
                  <span className="hidden sm:inline">Share</span>
                </Button>
              </div>

              {/* Plain-English translations of every pro insight */}
              <TranslatedInsights
                candles={candles}
                plan={plan}
                kind={best.kind}
                currentPrice={currentPrice}
                cashBalance={user?.cash_balance}
                patternName={best.name}
                onSetAlert={setPriceAlert}
              />

              {/* Collapsible "Why does this work?" for the curious */}
              <button
                onClick={() => setWhyOpen((v) => !v)}
                data-testid="copilot-why-toggle"
                className="w-full flex items-center justify-between p-3 rounded-2xl border border-border hover:bg-secondary/40 transition-colors text-left">
                <span className="text-sm font-medium flex items-center gap-2">
                  <Info className="w-4 h-4 text-[hsl(var(--ai))]" />
                  Why does this pattern usually work?
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${whyOpen ? "rotate-180" : ""}`} />
              </button>
              {whyOpen && (
                <Card className="p-5 rounded-2xl bg-[hsl(var(--ai))]/5 border-[hsl(var(--ai))]/20" data-testid="copilot-why-content">
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    {data.narration_ai || data.narration}
                  </p>
                  <div className="pt-3 border-t border-[hsl(var(--ai))]/20 text-xs text-muted-foreground">
                    <div className="font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                      <ShieldAlert className="w-3.5 h-3.5" /> Two rules to trade with confidence
                    </div>
                    <div className="space-y-1">
                      <div>• <span className="text-foreground">Wait for the price to reach {money(plan.entry)}</span> — never chase.</div>
                      <div>• <span className="text-foreground">Risk at most 1-2% of your account</span> on this single trade.</div>
                    </div>
                  </div>
                </Card>
              )}
            </>
          ) : (
            /* PRO MODE — full detail as before */
            <>
              <Card className="p-5 rounded-2xl" data-testid="copilot-headline">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {isBull ? (
                        <Badge className="bg-[hsl(var(--gain))]/20 text-[hsl(var(--gain))] border-[hsl(var(--gain))]/40">
                          <TrendingUp className="w-3 h-3 mr-1" /> BULLISH
                        </Badge>
                      ) : (
                        <Badge className="bg-[hsl(var(--loss))]/20 text-[hsl(var(--loss))] border-[hsl(var(--loss))]/40">
                          <TrendingDown className="w-3 h-3 mr-1" /> BEARISH
                        </Badge>
                      )}
                      <span className="font-display text-lg font-semibold">{best.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="copilot-narration">
                      {data.narration_ai || data.narration}
                    </p>
                  </div>
                  <Verdict score={best.confidence} kind={best.kind} />
                </div>
              </Card>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: isBull ? "Buy at" : "Short at", value: money(plan?.entry), color: "hsl(var(--gain))", testid: "copilot-entry" },
                  { label: "Stop-loss", value: money(plan?.stop), color: "hsl(var(--loss))", testid: "copilot-stop" },
                  { label: "Target", value: money(plan?.target), color: "hsl(var(--ai))", testid: "copilot-target" },
                  { label: "Risk / Reward", value: `1:${F(plan?.rr, 2)}`, color: "hsl(var(--foreground))", testid: "copilot-rr" },
                ].map((s) => (
                  <div key={s.testid} data-testid={s.testid}
                       className="p-3 rounded-xl border-2 bg-secondary/40"
                       style={{ borderColor: s.color, color: s.color, backgroundColor: `${s.color.startsWith("hsl") ? s.color.replace(")", " / 0.08)") : s.color}` }}>
                    <div className="text-[10px] uppercase tracking-widest opacity-80">{s.label}</div>
                    <div className="font-mono-tab text-lg font-bold mt-0.5">{s.value}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                {onPrefillOrder && plan && (
                  <Button
                    data-testid="copilot-prefill-btn"
                    onClick={() => onPrefillOrder({ side: isBull ? "buy" : "sell", price: plan.entry, stop: plan.stop })}
                    className={`rounded-full flex-1 ${isBull ? "bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90" : "bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90"} text-background font-semibold`}>
                    <Target className="w-4 h-4 mr-2" />
                    {isBull ? "Prefill buy order" : "Prefill short order"} at {money(plan.entry)}
                  </Button>
                )}
                <Button onClick={shareSetup} disabled={sharing} variant="outline" className="rounded-full" data-testid="copilot-share-btn">
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                  Share this setup
                </Button>
              </div>

              {(data?.alternates || []).length > 0 && (
                <Card className="p-4 rounded-2xl" data-testid="copilot-alternates">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Other setups on this chart</div>
                  <div className="space-y-1.5">
                    {data.alternates.map((a, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-secondary/40 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${a.kind === "bull" ? "bg-[hsl(var(--gain))]/20 text-[hsl(var(--gain))]" : "bg-[hsl(var(--loss))]/20 text-[hsl(var(--loss))]"}`}>
                            {a.kind}
                          </span>
                          <span className="font-medium">{a.name}</span>
                        </div>
                        <div className="flex items-center gap-3 font-mono-tab text-xs text-muted-foreground">
                          <span>Entry <span className="text-foreground">{money(a.plan?.entry)}</span></span>
                          <span>{a.confidence}%</span>
                          <ChevronRight className="w-3 h-3" />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
